import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	PositionDirection,
	BigNum,
	getTriggerPrice,
	BN,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { SaveTpSlOrdersCallback } from 'src/components/TradeForm/TpSlOrderForm/types';
import useTpSlOrderFormState from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormState';
import useTpSlOrderFormCalcs from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormCalcs';
import useTpSlOrderFormHandlers from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormHandlers';
import useTpSlOrderFormSideEffects from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormSideEffects';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useIsStopLossBeyondLiquidationPrice from 'src/components/TradeForm/TpSlOrderForm/hooks/useIsStopLossBeyondLiquidationPrice';
import { DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS } from 'src/components/TradeForm/TpSlOrderForm/consts';
import useMarkPrice from 'src/hooks/useMarkPrice';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { useMemo } from 'react';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';

interface Params {
	direction: 'long' | 'short';
	entryPrice: BigNum;
	entrySize: BigNum;
	saveOrdersCallback: SaveTpSlOrdersCallback;
	savedTakeProfitPrice: BigNum | null | undefined;
	savedStopLossPrice: BigNum | null | undefined;
	savedTakeProfitLimitPrice?: BigNum | null | undefined;
	savedStopLossLimitPrice?: BigNum | null | undefined;
	marketIndex?: number;
	allowTrailingStopLoss?: boolean;
}

export default function useTpSlOrderForm({
	direction,
	entryPrice,
	entrySize,
	saveOrdersCallback,
	savedTakeProfitPrice,
	savedStopLossPrice,
	savedTakeProfitLimitPrice,
	savedStopLossLimitPrice,
	marketIndex,
	allowTrailingStopLoss = false,
}: Params) {
	const selectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);

	const [triggerPercentBasis] = useSyncLocalStorage(
		'tradeFormTpSlTriggerPercentBasis',
		DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS
	);

	const positionIsLong = direction === 'long';

	const baseOrderDirection = positionIsLong
		? PositionDirection.LONG
		: PositionDirection.SHORT;

	const triggerOrderDirection = positionIsLong
		? PositionDirection.SHORT
		: PositionDirection.LONG;

	const { calcPercent, calcPrice } = useTpSlOrderFormCalcs({
		triggerPercentBasis,
		positionIsLong,
		entryPrice,
		entrySize,
		marketIndex: marketIndex ?? selectedMarketId?.marketIndex,
	});

	// Get velocity client and oracle price for trigger price calculation
	const velocityClient = useVelocityClient();
	const effectiveMarketId =
		marketIndex !== undefined
			? MarketId.createPerpMarket(marketIndex)
			: selectedMarketId;

	// Get fallback mark price first
	const fallbackMarkPrice = useMarkPrice(effectiveMarketId);
	const oraclePrice = useOraclePrice(effectiveMarketId);

	// Use primitives for the deps as otherwise they're referentially unstable.
	const fallbackMarkPriceNum = fallbackMarkPrice.toNum();
	const oraclePriceNum = oraclePrice.toNum();

	// Calculate reference price using the same logic as the modal
	const markPrice = useMemo(() => {
		const fallbackPrice = fallbackMarkPrice || ZERO_PRICE_BIGNUM;

		if (!velocityClient || oraclePrice.eqZero() || marketIndex === undefined) {
			return fallbackPrice;
		}

		try {
			const marketAccount = velocityClient.getPerpMarketAccount(marketIndex);
			if (!marketAccount) {
				return fallbackPrice;
			}

			const now = new BN(Math.floor(Date.now() / 1000)); // Current timestamp in seconds
			const referencePriceBN = getTriggerPrice(
				marketAccount,
				oraclePrice.val,
				now,
				true
			);

			return new BigNum(referencePriceBN, PRICE_PRECISION_EXP);
		} catch (error) {
			console.warn('Error calculating trigger price:', error);

			return fallbackPrice;
		}
	}, [velocityClient, marketIndex, fallbackMarkPriceNum, oraclePriceNum]);

	const {
		priceStringValue: takeProfitPriceStringValue,
		percentStringValue: takeProfitPercentStringValue,
		percent: takeProfitPercent,
		price: takeProfitPrice,
		limitPriceStringValue: takeProfitLimitPriceStringValue,
		limitPrice: takeProfitLimitPrice,
		priceInvalid: takeProfitPriceInvalid,
		priceInvalidReason: takeProfitPriceInvalidReason,
		limitPriceInvalid: takeProfitLimitPriceInvalid,
		limitPriceInvalidReason: takeProfitLimitPriceInvalidReason,
		inputSource: takeProfitInputSource,
		clearState: clearTakeProfitState,
		setState: setTakeProfitState,
	} = useTpSlOrderFormState({
		savedPrice: savedTakeProfitPrice,
		savedLimitPrice: savedTakeProfitLimitPrice,
		calcPercent,
	});

	const {
		priceStringValue: stopLossPriceStringValue,
		percentStringValue: stopLossPercentStringValue,
		percent: stopLossPercent,
		price: stopLossPrice,
		limitPriceStringValue: stopLossLimitPriceStringValue,
		limitPrice: stopLossLimitPrice,
		priceInvalid: stopLossPriceInvalid,
		priceInvalidReason: stopLossPriceInvalidReason,
		limitPriceInvalid: stopLossLimitPriceInvalid,
		limitPriceInvalidReason: stopLossLimitPriceInvalidReason,
		inputSource: stopLossInputSource,
		clearState: clearStopLossState,
		setState: setStopLossState,
	} = useTpSlOrderFormState({
		savedPrice: savedStopLossPrice,
		savedLimitPrice: savedStopLossLimitPrice,
		calcPercent,
	});

	const takeProfitInvalid =
		!!takeProfitPriceStringValue &&
		(takeProfitPriceInvalid || takeProfitLimitPriceInvalid);
	const stopLossInvalid =
		!!stopLossPriceStringValue &&
		(stopLossPriceInvalid || stopLossLimitPriceInvalid);
	const takeProfitInvalidReason =
		takeProfitPriceInvalidReason ?? takeProfitLimitPriceInvalidReason;
	const stopLossInvalidReason =
		stopLossPriceInvalidReason ?? stopLossLimitPriceInvalidReason;

	const { updatePrice, updatePercent, updateLimitPrice, updateOrders } =
		useTpSlOrderFormHandlers({
			calcPercent,
			calcPrice,
			clearTakeProfitState,
			clearStopLossState,
			setTakeProfitState,
			setStopLossState,
			positionIsLong,
			entryPrice,
			markPrice,
			takeProfitPriceStringValue,
			takeProfitInvalid,
			stopLossPriceStringValue,
			stopLossInvalid,
			takeProfitPrice,
			takeProfitPercent,
			takeProfitLimitPrice,
			stopLossPrice,
			stopLossPercent,
			stopLossLimitPrice,
			savedTakeProfitPrice,
			savedStopLossPrice,
			triggerOrderDirection,
			saveOrdersCallback,
			allowTrailingStopLoss,
			marketIndex,
		});

	useTpSlOrderFormSideEffects({
		direction,
		selectedMarketId,
		triggerPercentBasis,
		saveOrdersCallback,
		updateOrders,
		clearTakeProfitState,
		clearStopLossState,
		calcPercent,
		calcPrice,
		setTakeProfitState,
		setStopLossState,
		takeProfitPrice,
		stopLossPrice,
		takeProfitInputSource,
		stopLossInputSource,
		takeProfitLimitPrice,
		stopLossLimitPrice,
		entryPrice,
		markPrice,
		allowTrailingStopLoss,
	});

	const isStopLossBeyondLiquidationPrice =
		useIsStopLossBeyondLiquidationPrice();

	const formDisabled = !entrySize || entrySize.eqZero();

	return {
		takeProfitPriceStringValue,
		takeProfitPercentStringValue,
		takeProfitPrice,
		takeProfitLimitPrice,
		takeProfitLimitPriceStringValue,
		takeProfitInvalid,
		takeProfitInvalidReason,
		stopLossPriceStringValue,
		stopLossPercentStringValue,
		stopLossPrice,
		stopLossLimitPrice,
		stopLossLimitPriceStringValue,
		stopLossInvalid,
		stopLossInvalidReason,
		updatePrice,
		updatePercent,
		updateLimitPrice,
		updateOrders,
		triggerPercentBasis,
		triggerOrderDirection,
		baseOrderDirection,
		selectedMarketId,
		positionIsLong,
		isStopLossBeyondLiquidationPrice,
		formDisabled,
	};
}
